import { useBreakpointValue } from "@gluestack-style/react";
import { zodResolver } from "@hookform/resolvers/zod";
import useToast from "@repo/core/components/core/Toast";
import { IdentityInputTypeEnum } from "@repo/core/components/forms/IdentityTypeInput/types";
import { PaymentTypeEnum } from "@repo/core/enums/payments";
import { useIdentityTypeInput } from "@repo/core/hooks/useIdentityTypeInput";
import { transformCardExpirationDate } from "@repo/core/hooks/useIdentityTypeInput/utils";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { ROUTES } from "@repo/core/routes";
import { CardConsultDataResponseDTO } from "@repo/core/services/entities/Card";
import { InvoiceAndDealPaymentType } from "@repo/core/services/entities/Terminal";
import { apiInstance } from "@repo/core/services/instances/api";
import { ClientResource } from "@repo/core/services/resources/client";
import { AxiosError } from "axios";
import { useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { z } from "zod";
import { ConsultProfileClientResponseDTO } from "@repo/core/services/entities/Client";
import { getInvoiceMinValue, getTitle, getDealMinValue } from "./utils";
import { isValidCard, isValidCPF } from "@repo/core/helpers/zod.helpers";
import {
  validatePaymentValue,
  PaymentValidationParams
} from "@repo/core/helpers/zod.invoicePaymentValue.helpers";
import { cardFormSchema, cpfFormSchema } from "@repo/core/helpers/zod.schemas";

const clientResource = new ClientResource(apiInstance);

export const InvoicePaymentController = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [invoiceInfo, setInvoiceInfo] = useState<
    CardConsultDataResponseDTO["objFatura"] | undefined
  >(undefined);

  const [dealInfo, setDealInfo] = useState<
    ConsultProfileClientResponseDTO[0]["lstAcordos"][0] | undefined
  >(undefined);
  const { back, replace, push } = useNavigation();

  const state = useRouteParams();

  const paymentType: PaymentTypeEnum = state.paymentType;

  const title = getTitle(paymentType);

  const getInvoiceOrDealInfo = async (
    productInfoData?: CardConsultDataResponseDTO
  ) => {
    if (productInfoData) {
      try {
        if (paymentType === PaymentTypeEnum.INVOICE) {
          setInvoiceInfo(productInfoData.objFatura);
        } else {
          setIsLoading(true);
          const { data: profileResponse } = await clientResource.consultProfile(
            productInfoData.strCpfPortador
          );

          const productClient = profileResponse.find(
            (productClient) =>
              productInfoData?.strCpfPortador === productClient.strCliente
          );

          if (productClient) {
            // Always get the most recent deal if somehow we have multiple items
            const deal = productClient?.lstAcordos.reverse()[0];
            setDealInfo(deal);
          }
        }
      } catch (err: unknown) {
        error("O cartão informado não possui acordos");
      } finally {
        setIsLoading(false);
      }
    }
  };
  const onSelectProduct = async (
    productInfoData?: CardConsultDataResponseDTO
  ) => {
    getInvoiceOrDealInfo(productInfoData);
  };

  const {
    selectedIdentityInputType,
    productInfo,
    handleOnChangeIdentityType,
    handleOnSelectProduct
  } = useIdentityTypeInput({ onSelectProduct });

  const { error } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const invoiceMinValue = getInvoiceMinValue(invoiceInfo);

  const dealMinValue = getDealMinValue(dealInfo);

  const invoiceSchema = z.object({
    paymentValue: z
      .string({ required_error: "É necessário informar o valor do pagamento." })
      .superRefine((value, ctx) => {
        const validationParams: PaymentValidationParams = {
          value,
          paymentType,
          invoiceInfo,
          dealInfo,
          invoiceMinValue: invoiceMinValue,
          dealMinValue: dealMinValue
        };
        const issues = validatePaymentValue(validationParams);
        issues.forEach((issue) => ctx.addIssue(issue));
      })
  });

  const formSchema =
    selectedIdentityInputType === IdentityInputTypeEnum.CPF
      ? invoiceSchema.merge(cpfFormSchema)
      : invoiceSchema.merge(cardFormSchema);

  const {
    control,
    formState: { isValid, isSubmitting },
    handleSubmit
  } = useForm({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    reValidateMode: "onBlur"
  });

  const handleGoToSales = () => {
    replace(ROUTES.sales);
  };

  const getCPFFormPayload = (formData: FieldValues) => {
    if (productInfo) {
      const transformedCardExpiryDate = transformCardExpirationDate(
        productInfo.datDataValidade
      );

      const payload: InvoiceAndDealPaymentType = {
        strPlastico: productInfo?.strPlasticoTitular,
        dblValor: formData.paymentValue,
        strValidade: transformedCardExpiryDate,
        strTipoOperacao: paymentType === PaymentTypeEnum.DEAL ? "R" : "F"
      };
      return payload;
    }
  };

  const getCardFormPayload = (formData: FieldValues) => {
    const payload: InvoiceAndDealPaymentType = {
      strPlastico: formData.card,
      dblValor: formData.paymentValue,
      strValidade: formData.cardExpirationDate,
      strTipoOperacao: paymentType === PaymentTypeEnum.DEAL ? "R" : "F"
    };

    return payload;
  };

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    try {
      const payload =
        selectedIdentityInputType === IdentityInputTypeEnum.CPF
          ? getCPFFormPayload(formData)
          : getCardFormPayload(formData);

      if (payload) {
        push(ROUTES.invoicePaymentConfirm, payload);
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response?.data?.strMensagem) {
        error(err.response.data.strMensagem);
        return;
      }
      error("Cartão inválido, tente novamente.");
    }
  });

  const onChangeIdentityType = (identity: string) => {
    handleOnChangeIdentityType(identity as IdentityInputTypeEnum);
    setInvoiceInfo(undefined);
    setDealInfo(undefined);
  };
  const onFetchCard = () => {
    setIsLoading(true);
  };

  const onFetchProducts = () => {
    setIsLoading(true);
  };

  const onFetchCardFinish = async (
    productInfo?: CardConsultDataResponseDTO
  ) => {
    await getInvoiceOrDealInfo(productInfo);
  };

  const onFetchProductsFinish = async () => {
    setIsLoading(false);
  };

  const onChangeCard = (value: string) => {
    const isValid = isValidCard(value);
    if (!isValid) {
      if (paymentType === PaymentTypeEnum.INVOICE) {
        setInvoiceInfo(undefined);
      } else {
        setDealInfo(undefined);
      }
    }
  };

  const onChangeCPF = (value: string) => {
    const isValid = isValidCPF(value);
    if (!isValid) {
      if (paymentType === PaymentTypeEnum.INVOICE) {
        setInvoiceInfo(undefined);
      } else {
        setDealInfo(undefined);
      }
    }
  };

  return {
    title,
    back,
    isMobile,
    handleGoToSales,
    control,
    onChangeIdentityType,
    selectedIdentityInputType,
    handleOnSelectProduct,
    isValid,
    isSubmitting,
    handleOnSubmitForm,
    isLoading,
    invoiceInfo,
    dealInfo,
    onFetchCard,
    onFetchProducts,
    onFetchCardFinish,
    onFetchProductsFinish,
    onChangeCard,
    onChangeCPF,
    paymentType,
    invoiceMinValue
  };
};
